import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, PenLine, Volume2, TrendingUp, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const FEATURES = [
  {
    icon: BookOpen,
    title: { uz: 'Bosqichma-bosqich darslar', ru: 'Уроки по шагам', en: 'Guided lessons' },
    body: {
      uz: 'Boshlangʻich darajadan boshlab, oʻz surʻatingizda ilgarilang.',
      ru: 'Начните с азов и продвигайтесь в своём темпе.',
      en: 'Start from the basics and move at your own pace.',
    },
  },
  {
    icon: PenLine,
    title: { uz: 'Qoʻlda yozish mashqi', ru: 'Практика письма', en: 'Handwriting practice' },
    body: {
      uz: '28 ta arab harfini barmogʻingiz bilan chizib oʻrganing.',
      ru: 'Учите все 28 арабских букв, обводя их пальцем.',
      en: 'Trace all 28 Arabic letters by hand, stroke by stroke.',
    },
  },
  {
    icon: Volume2,
    title: { uz: 'Talaffuzni tinglash', ru: 'Слушайте произношение', en: 'Hear the sounds' },
    body: {
      uz: 'Har bir harfning haqiqiy talaffuzini eshiting.',
      ru: 'Слушайте настоящее произношение каждой буквы.',
      en: 'Listen to the real pronunciation of every letter.',
    },
  },
  {
    icon: TrendingUp,
    title: { uz: 'Taraqqiyotni kuzating', ru: 'Следите за прогрессом', en: 'Track your progress' },
    body: {
      uz: 'Statistikangiz orqali oʻsishingizni koʻring.',
      ru: 'Наблюдайте за своим ростом через статистику.',
      en: 'Watch yourself improve, lesson by lesson.',
    },
  },
];

function Home() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const lang = ['uz', 'ru', 'en'].includes(i18n.language) ? i18n.language : 'uz';

  return (
    <div>
      <section className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800 bg-brand-50/60 dark:bg-stone-900">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='84' height='84' viewBox='0 0 84 84'%3E%3Cg fill='none' stroke='%2326544a' stroke-width='1.2'%3E%3Crect x='12' y='12' width='40' height='40'/%3E%3Crect x='12' y='12' width='40' height='40' transform='rotate(45 32 32)'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
        <div className="container relative mx-auto px-4 py-20 md:py-28 text-center">
          <h1 className="font-display text-4xl md:text-6xl font-semibold text-brand-900 dark:text-brand-200 mb-5 text-balance">
            {t('app_name')}
          </h1>
          <p className="text-lg md:text-xl text-stone-600 dark:text-stone-300 mb-10 max-w-xl mx-auto">
            {user
              ? { uz: `Xush kelibsiz, ${user.fullName}!`, ru: `С возвращением, ${user.fullName}!`, en: `Welcome back, ${user.fullName}!` }[lang]
              : { uz: 'Oʻzingizga qulay tilda arab tilini oʻrganing', ru: 'Изучайте арабский на удобном вам языке', en: 'Learn Arabic in your preferred language' }[lang]}
          </p>

          {!user ? (
            <div className="flex justify-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-brand-700 text-white px-7 py-3 rounded-full font-semibold hover:bg-brand-800 transition-colors"
              >
                {t('auth.register')}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center bg-white dark:bg-stone-800 text-brand-800 dark:text-brand-200 border border-stone-200 dark:border-stone-700 px-7 py-3 rounded-full font-semibold hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
              >
                {t('auth.login')}
              </Link>
            </div>
          ) : (
            <Link
              to="/lessons"
              className="inline-flex items-center gap-2 bg-brand-700 text-white px-7 py-3 rounded-full font-semibold hover:bg-brand-800 transition-colors"
            >
              {t('nav.lessons')}
              <ArrowRight size={18} />
            </Link>
          )}
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title.en}
              className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6 hover:border-brand-200 dark:hover:border-brand-700 hover:shadow-md transition-all"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 mb-4">
                <Icon size={22} strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-semibold text-stone-800 dark:text-stone-100 mb-1.5">{title[lang]}</h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">{body[lang]}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Home;
